import { useState, useEffect, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { mcpTokensApi } from '../../services/integrations';
import { formatDateTime } from '../../i18n/format';
import { MCP_TOOL_CATALOG } from './mcpToolCatalog';
import type { McpTokenView, McpTokenScope } from '@traaker/shared';

/** Derives the MCP server URL from the same env-specific API base every other
 *  build already carries (VITE_API_URL, checked in per mode — see
 *  .env.development / .env.production) rather than hardcoding the prod
 *  domain, which would silently be wrong on a dev build. Same api./mcp.
 *  subdomain-per-service convention as auth./api. */
function mcpServerUrl(): string {
  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.traaker.ai/api';
  return apiUrl.replace(/^https:\/\/api\./, 'https://mcp.').replace(/\/api$/, '/mcp');
}

/** The full, ready-to-paste `claude mcp add` command with the real token
 *  already spliced in — a customer should never have to hand-edit a
 *  template with their own secret. */
function buildSetupCommand(token: string): string {
  return `claude mcp add --transport http traaker ${mcpServerUrl()} --header "Authorization: Bearer ${token}"`;
}

/** Renders the MCP section of the Intégrations tab, or an upsell card when
 *  the tenant isn't entitled — mirrors WebhooksIntegration's list+dialog
 *  shape (reveal-once secret, no re-display after creation). */
export default function McpIntegration({ entitled, conversationsEnabled }: { entitled: boolean; conversationsEnabled?: boolean }) {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<McpTokenView[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTokens(await mcpTokensApi.list());
    } catch {
      /* no-op */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (entitled) load();
  }, [entitled, load]);

  if (!entitled) {
    return (
      <Box sx={{ maxWidth: 760, mt: 4 }}>
        <Typography variant="h6" fontWeight={700}>
          {t('mcpTokens.title')}
        </Typography>
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('mcpTokens.upsell')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 760, mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('mcpTokens.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('mcpTokens.intro')}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setAddOpen(true)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {t('mcpTokens.add')}
        </Button>
      </Box>

      <ToolCatalog conversationsEnabled={conversationsEnabled} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tokens.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('mcpTokens.empty')}
        </Alert>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {tokens.map((tk) => (
            <TokenRow key={tk.id} token={tk} onChange={load} />
          ))}
        </Stack>
      )}

      <AddTokenDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={load} />
    </Box>
  );
}

/** Read-only Chip pair — green "lecture" for read tools, amber "écriture"
 *  for write tools — reused by both the catalog rows and the token-scope
 *  chip so the same visual language means the same thing everywhere on
 *  this page. */
function ScopeChip({ scope, size = 'small' }: { scope: McpTokenScope; size?: 'small' | 'medium' }) {
  const { t } = useTranslation();
  const isRead = scope === 'read';
  return (
    <Chip
      size={size}
      icon={isRead ? <VisibilityIcon sx={{ fontSize: 15 }} /> : <EditIcon sx={{ fontSize: 15 }} />}
      label={isRead ? t('mcpTokens.scope.read') : t('mcpTokens.scope.readWrite')}
      color={isRead ? 'default' : 'warning'}
      variant={isRead ? 'outlined' : 'filled'}
      sx={isRead ? {} : { fontWeight: 600 }}
    />
  );
}

/** Collapsible catalog of every tool Claude can call through the connection
 *  — split into "Lecture" / "Écriture" groups, each row pairing the tool's
 *  purpose with a natural-language example of how a customer would actually
 *  phrase it in a Claude conversation. Closed by default so the page reads
 *  as "generate a token" first, "see what it can do" second. */
function ToolCatalog({ conversationsEnabled }: { conversationsEnabled?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const visible = MCP_TOOL_CATALOG.filter((tool) => !tool.conversationsOnly || conversationsEnabled);
  const readTools = visible.filter((tool) => tool.readOnly);
  const writeTools = visible.filter((tool) => !tool.readOnly);

  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none', pl: 0 }}
      >
        {t('mcpTokens.tools.toggle', { count: MCP_TOOL_CATALOG.length })}
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <ToolGroup titleKey="mcpTokens.tools.groupRead" tools={readTools} />
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />
          <ToolGroup titleKey="mcpTokens.tools.groupWrite" tools={writeTools} />
        </Box>
      </Collapse>
    </Box>
  );
}

function ToolGroup({ titleKey, tools }: { titleKey: string; tools: typeof MCP_TOOL_CATALOG }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3 }}>
          {t(titleKey).toUpperCase()}
        </Typography>
      </Box>
      <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
        {tools.map((tool) => (
          <Box key={tool.name} sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                component="code"
                sx={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 13,
                  fontWeight: 700,
                  bgcolor: tool.readOnly ? 'action.hover' : '#FEF3C7',
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 0.75,
                }}
              >
                {tool.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(tool.descriptionKey)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, fontStyle: 'italic' }}
            >
              “{t(tool.exampleKey)}”
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function TokenRow({ token, onChange }: { token: McpTokenView; onChange: () => void }) {
  const { t } = useTranslation();
  const remove = async () => {
    await mcpTokensApi.revoke(token.id).catch(() => {});
    onChange();
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{token.name}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            <ScopeChip scope={token.scope} />
            <Chip size="small" label={token.tokenHint} variant="outlined" sx={{ fontFamily: 'ui-monospace, monospace' }} />
            <Chip size="small" label={t('mcpTokens.createdAt', { date: formatDateTime(token.createdAt) })} variant="outlined" />
            <Chip
              size="small"
              label={
                token.lastUsedAt
                  ? t('mcpTokens.lastUsedAt', { date: formatDateTime(token.lastUsedAt) })
                  : t('mcpTokens.neverUsed')
              }
              variant="outlined"
            />
          </Box>
        </Box>
        <IconButton size="small" color="error" onClick={remove} title={t('mcpTokens.revoke')}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

function AddTokenDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<McpTokenScope>('read_write');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [createdToken, setCreatedToken] = useState('');

  const reset = () => {
    setName('');
    setScope('read_write');
    setErr('');
    setCreatedToken('');
  };
  const close = () => {
    reset();
    onClose();
  };

  const create = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await mcpTokensApi.create({ name: name || undefined, scope });
      setCreatedToken(res.token);
      onCreated();
    } catch (e: any) {
      setErr(e.message || t('mcpTokens.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('mcpTokens.dialog.title')}</DialogTitle>
      <DialogContent>
        {createdToken ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">{t('mcpTokens.dialog.tokenWarning')}</Alert>
            <TextField
              label={t('mcpTokens.dialog.tokenLabel')}
              value={createdToken}
              fullWidth
              size="small"
              InputProps={{ readOnly: true, sx: { fontFamily: 'ui-monospace, monospace' } }}
            />
            <Button variant="contained" onClick={() => { navigator.clipboard.writeText(createdToken).catch(() => {}); }}>
              {t('mcpTokens.dialog.copyToken')}
            </Button>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {t('mcpTokens.dialog.setupHintLabel')}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  borderRadius: 1,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  overflowX: 'auto',
                  fontFamily: 'ui-monospace, monospace',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {buildSetupCommand(createdToken)}
              </Box>
              <Button
                size="small"
                sx={{ mt: 0.5 }}
                onClick={() => { navigator.clipboard.writeText(buildSetupCommand(createdToken)).catch(() => {}); }}
              >
                {t('mcpTokens.dialog.copyCommand')}
              </Button>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {err && <Alert severity="error">{err}</Alert>}
            <TextField
              label={t('mcpTokens.dialog.nameLabel')}
              placeholder="Claude Code — laptop"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText={t('mcpTokens.dialog.nameHelper')}
            />
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                {t('mcpTokens.dialog.scopeLabel')}
              </Typography>
              <ToggleButtonGroup
                value={scope}
                exclusive
                fullWidth
                size="small"
                onChange={(_e, next) => { if (next) setScope(next); }}
              >
                <ToggleButton value="read_write" sx={{ textTransform: 'none', justifyContent: 'flex-start', gap: 1 }}>
                  <EditIcon fontSize="small" />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" fontWeight={600}>{t('mcpTokens.scope.readWrite')}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('mcpTokens.dialog.scopeReadWriteHelper')}
                    </Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="read" sx={{ textTransform: 'none', justifyContent: 'flex-start', gap: 1 }}>
                  <VisibilityIcon fontSize="small" />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" fontWeight={600}>{t('mcpTokens.scope.read')}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('mcpTokens.dialog.scopeReadHelper')}
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {createdToken ? (
          <Button onClick={close} variant="contained">
            {t('mcpTokens.dialog.done')}
          </Button>
        ) : (
          <>
            <Button onClick={close}>{t('mcpTokens.dialog.cancel')}</Button>
            <Button
              variant="contained"
              onClick={create}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('mcpTokens.dialog.create')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
